import { useCallback, useRef, useState } from "react";

export function usePortfolioGames() {
  const [workUnlocked, setWorkUnlocked] = useState(false);
  const [workError, setWorkError] = useState(false);
  const [workFailCount, setWorkFailCount] = useState(0);

  const [fightOpen, setFightOpen] = useState(false);
  const [fightRound, setFightRound] = useState(1);
  const [playerHP, setPlayerHP] = useState(2);
  const [bossHP, setBossHP] = useState(2);
  const [fightMessage, setFightMessage] = useState("");
  const [fightWon, setFightWon] = useState(false);

  const [projectsUnlocked, setProjectsUnlocked] = useState(false);
  const [projectsWrong, setProjectsWrong] = useState(false);

  const [shootOpen, setShootOpen] = useState(false);

  const [resumeModalOpen, setResumeModalOpen] = useState(false);

  const [contactPageOpen, setContactPageOpen] = useState(false);
  const [contactSent, setContactSent] = useState(false);
  const [contactType, setContactType] = useState("");
  const [contactName, setContactName] = useState("");

  const workInputRef = useRef(null);

  const unlockedCount = (workUnlocked ? 1 : 0) + (projectsUnlocked ? 1 : 0);

  const workQuestion =
    workFailCount >= 2
      ? "okay okay, hold on... what's 1+1?"
      : "Prove you belong here. Type the command that lists all running containers.";

  const checkWork = useCallback(() => {
    const val = (workInputRef.current?.value ?? "").trim().toLowerCase();
    const expected = workFailCount >= 2 ? "2" : "docker ps";
    if (val === expected) {
      setWorkUnlocked(true);
      setWorkError(false);
    } else {
      setWorkError(true);
      setWorkFailCount((c) => c + 1);
      if (workInputRef.current) workInputRef.current.value = "";
    }
  }, [workFailCount]);

  const openFight = () => {
    setFightOpen(true);
    setFightRound(1);
    setPlayerHP(2);
    setBossHP(2);
    setFightMessage("");
    setFightWon(false);
  };

  const claimVictory = () => {
    setWorkUnlocked(true);
    setFightOpen(false);
  };

  const fightResolve = (isCorrect) => {
    if (isCorrect) {
      setBossHP((bhp) => {
        const next = bhp - 1;
        if (next <= 0) {
          setFightWon(true);
          setFightMessage("KO! You beat the bug. 🏆");
          return 0;
        }
        setFightRound(2);
        setFightMessage("Critical hit! 💥 One bug down.");
        return next;
      });
    } else {
      setPlayerHP((hp) => {
        const next = hp - 1;
        if (next <= 0) {
          setPlayerHP(2);
          setFightRound(1);
          setFightMessage("You got wrecked 💀 run it back.");
          return 2;
        }
        setFightMessage("Ouch, that missed. -1 HP");
        return next;
      });
    }
  };

  const startShoot = useCallback(() => setShootOpen(true), []);
  const closeShoot = useCallback(() => setShootOpen(false), []);
  const claimShootVictory = useCallback(() => {
    setProjectsUnlocked(true);
    setShootOpen(false);
  }, []);

  const openResumeModal = () => setResumeModalOpen(true);

  const confirmResume = () => {
    setResumeModalOpen(false);
    setContactPageOpen(true);
  };

  const submitContact = (form) => {
    setContactName(form.name);
    setContactSent(true);
  };

  const fightPrompt =
    fightRound === 1
      ? "🐛 A wild bug appears! What's your first move?"
      : "💀 Boss bug: infinite loop! Finish it off:";

  const fightOptions =
    fightRound === 1
      ? [
          { label: "Read the stack trace 🔍", correct: true },
          { label: "console.log everything 🖨️", correct: false },
          { label: "git blame 👉", correct: false },
        ]
      : [
          { label: "Add a break condition 🛑", correct: true },
          { label: "Restart the laptop 🔌", correct: false },
          { label: "Blame the intern 🙋", correct: false },
        ];

  return {
    workUnlocked,
    workError,
    workFailCount,
    workQuestion,
    workInputRef,
    checkWork,
    bypassWork: () => setWorkUnlocked(true),

    fightOpen,
    fightNotOpen: !fightOpen,
    fightRound,
    fightWon,
    fightMessage,
    fightPrompt,
    fightOptions,
    playerHearts: "❤️".repeat(Math.max(playerHP, 0)) || "💀",
    bossHearts: "🐞".repeat(Math.max(bossHP, 0)) || "☠️",
    openFight,
    claimVictory,
    fightResolve,

    projectsUnlocked,
    projectsWrong,
    tryApply: () => {
      setProjectsUnlocked(true);
      setProjectsWrong(false);
    },
    tryPush: () => setProjectsWrong(true),
    tryCommit: () => setProjectsWrong(true),

    shootOpen,
    startShoot,
    closeShoot,
    claimShootVictory,

    resumeModalOpen,
    openResumeModal,
    closeResumeModal: () => setResumeModalOpen(false),
    confirmResume,

    contactPageOpen,
    contactSent,
    contactType,
    contactName,
    setContactType,
    closeContactPage: () => {
      setContactPageOpen(false);
      setContactSent(false);
    },
    submitContact,

    unlockedCount,
  };
}
